import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getMyBids, getMission } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import { Loader2, Package, Calendar, Truck, ArrowRight } from 'lucide-react';

const MyBidsPage = () => {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBids();
  }, []);

  const fetchBids = async () => {
    try {
      const data = await getMyBids();
      setBids(data);
    } catch (error) {
      console.error('Failed to fetch bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', className: 'status-pending' },
      accepted: { label: 'Acceptée', className: 'status-accepted' },
      rejected: { label: 'Refusée', className: 'status-rejected' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100' };
    return <Badge className={`badge ${config.className}`}>{config.label}</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="my-bids-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
            Mes Offres
          </h1>
          <p className="text-slate-500 mt-1">
            Suivez l&apos;état de vos propositions
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="stat-value text-xl">{bids.length}</div>
                <div className="stat-label">Total</div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card bg-amber-50 border-amber-100">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="stat-value text-xl text-amber-600">
                  {bids.filter(b => b.status === 'pending').length}
                </div>
                <div className="stat-label">En attente</div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card bg-emerald-50 border-emerald-100">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="stat-value text-xl text-emerald-600">
                  {bids.filter(b => b.status === 'accepted').length}
                </div>
                <div className="stat-label">Acceptées</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bids List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : bids.length > 0 ? (
          <div className="space-y-4">
            {bids.map((bid) => (
              <Link key={bid.id} to={`/missions/${bid.mission_id}`} data-testid={`bid-card-${bid.id}`}>
                <Card className="hover:shadow-lg hover:border-orange-200 transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-slate-900">Mission #{bid.mission_id.slice(0, 8)}</span>
                          {getStatusBadge(bid.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Truck className="w-4 h-4" />
                            {bid.vehicle_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(bid.estimated_delivery).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        {bid.message && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-1">{bid.message}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-xs text-slate-500 uppercase tracking-wide">Votre offre</div>
                          <div className="font-['JetBrains_Mono'] font-bold text-xl text-slate-900">
                            {bid.proposed_price}€
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune offre</h3>
                <p className="text-slate-500">
                  Consultez les opportunités disponibles pour soumettre vos premières offres
                </p>
                <Link to="/opportunities">
                  <button className="mt-4 text-orange-500 hover:text-orange-600 font-medium">
                    Voir les opportunités →
                  </button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default MyBidsPage;
